import { useState } from 'react'
import { Box, Typography, TextField, Button, InputAdornment, IconButton, FormHelperText, Stack } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import Visibility from '@mui/icons-material/VisibilityOutlined'
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined'
import BasicModal from '@components/Modal/BasicModal'
import AlertConfirmModal from '@components/Modal/AlertConfirmModal'
import { myUpdatePassword, updateMonthChangePassword } from "@api/myPageAccount";
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@components/AuthContext';
import { useIsMobile } from '@/hooks/Responsive'
import { useConfirm } from '@components/ConfirmProvider'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

export default function PasswordChangeModal({ 
  open, 
  onClose, 
  id, 
  hideCloseButton = false, // 닫기 버튼 유무
  
  notice=false,  // 장기 미접속자 안내 문구
  longInactive = false // 장기 미접속 여부
}) {
  const { isMobile } = useIsMobile()
  const lang = useLang() 
  const navigate = useNavigate();
  const { alert, confirm } = useConfirm();
  const { logout } = useAuth();

  const [showOld, setShowOld] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showNewCheck, setShowNewCheck] = useState(false)
  const [alertOpen, setAlertOpen] = useState(false)

  const [oldPassword, setOldPassword] = useState('') // 현재 비밀번호
  const [oldPasswordError, setOldPasswordError] = useState(false) // 현재 비밀번호 오류 상태
  const [oldPasswordHelperText, setOldPasswordHelperText] = useState('') // 현재 비밀번호 오류 텍스트

  const [newPassword, setNewPassword] = useState('') // 새 비밀번호
  const [newPasswordError, setNewPasswordError] = useState(false) // 새 비밀번호 오류 상태
  const [newPasswordHelperText, setNewPasswordHelperText] = useState('') // 새 비밀번호 오류 텍스트
  
  const [newPasswordCheck, setNewPasswordCheck] = useState('') // 새 비밀번호 확인
  const [newPasswordCheckError, setNewPasswordCheckError] = useState(false) // 새 비밀번호 확인 오류 상태
  const [newPasswordCheckHelperText, setNewPasswordCheckHelperText] = useState('') // 새 비밀번호 확인 오류 텍스트

  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&+=])[A-Za-z\d!@#$%^&+=]{10,}$/

  // 리셋폼
  const resetForm = () => {
    setShowOld(false)
    setShowNew(false)
    setShowNewCheck(false)
    setAlertOpen(false)

    setOldPassword('')
    setOldPasswordError(false)
    setOldPasswordHelperText('')

    setNewPassword('')
    setNewPasswordError(false)
    setNewPasswordHelperText('')

    setNewPasswordCheck('')
    setNewPasswordCheckError(false)
    setNewPasswordCheckHelperText('')
  }
  
  // 현재 비밀번호 체크
  const oldPasswordValidate = () => {
    if (!oldPassword) {
      setOldPasswordError(true);
      setOldPasswordHelperText('비밀번호를 입력해 주세요.');
      return false;
    }
    else {
      setOldPasswordError(false);
      setOldPasswordHelperText('');
      return true;
    }
  };

  // 새 비밀번호 체크
  const newPasswordValidate = () => {
    if (!newPassword) {
      setNewPasswordError(true);
      setNewPasswordHelperText('새 비밀번호를 입력해 주세요.');
      return false;
    }
    else if (newPassword.length < 10) {
      setNewPasswordError(true);
      setNewPasswordHelperText('새 비밀번호를 10자이상 입력해 주세요.');
      return false;
    }
    else if (!passwordRegex.test(newPassword)) {
      setNewPasswordError(true);
      setNewPasswordHelperText('비밀번호 규정을 확인해 주세요.');
      return false;
    }
    else if (oldPassword == newPassword) {
      setNewPasswordError(true);
      setNewPasswordHelperText('현재 비밀번호와 동일합니다.');
      return false;
    }
    else {
      setNewPasswordError(false);
      setNewPasswordHelperText('');
      return true;
    }
  };

  // 새 비밀번호 확인 체크
  const newPasswordCheckValidate = () => {
    if (!newPasswordCheck) {
      setNewPasswordCheckError(true);
      setNewPasswordCheckHelperText('새 비밀번호 확인을 입력해 주세요.');
      return false;
    }
    else if (newPassword !== newPasswordCheck) {
      setNewPasswordCheckError(true);
      setNewPasswordCheckHelperText('새 비밀번호와 일치하지 않습니다.');
      return false;
    } else {
      setNewPasswordCheckError(false);
      setNewPasswordCheckHelperText('');
      return true;
    }
  };

  // 확인버튼 or 비밀번호변경 버튼
  const handleConfirm = async () => {
    // 각 검증을 먼저 호출
    const isOldValid = oldPasswordValidate();
    const isNewValid = newPasswordValidate();
    const isNewCheckValid = newPasswordCheckValidate();

    if (!(isOldValid && isNewValid && isNewCheckValid)) {
      return;
    }

    try {
      const params = {
        publicId : id,
        memberPassword : oldPassword,
        newPassword : newPassword,
      };

      const response = await myUpdatePassword(params);

      if (response?.success) {
        setAlertOpen(true);
      }
      else {
        alert(response?.message || "변경 중 오류가 발생했습니다.");
        // onClose();
      }
    } catch (e) {
      //console.error(e);
      alert(e?.response?.data?.message || e.message || "변경 중 오류가 발생했습니다.");
    }

  }

  // 30일후변경 버튼
  const handleMonthChangeConfirm = async () => {

    try {
      const params = {
        publicId : id,
      };

      const response = await updateMonthChangePassword(params);

      if (response?.success) {
        alert("비밀번호 변경이 한달 연장되었습니다.");
        onClose();
      }
      else {
        alert(response?.message || "변경 중 오류가 발생했습니다.");
        onClose();
      }
    } catch (e) {
      console.error(e);
    }
  }

  const handleAlertClose = async () => {
    try {
      setAlertOpen(false)
      onClose()
      await logout();  // 서버 /api/auth/logout 호출 + 상태 초기화
    } finally {
      navigate(buildLangHref('/', lang));
    }
  }

  return (
    <>
      <Box sx={{ display: alertOpen ? 'none' : 'block' }}>
        <BasicModal
          open={open}
          onClose={onClose}
          onReset={resetForm}
          title={longInactive ? '비밀번호 재설정' : '비밀번호 변경'}
          variant="bottomsheet"
          hideCloseButton={hideCloseButton}
          actions={
            longInactive ? (
              // 장기 미접속자
              <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ width: '100%' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={handleMonthChangeConfirm}
                  sx={{ flex: isMobile ? 1 : undefined }}
                >
                  한달후 변경
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={handleConfirm}
                  sx={{ flex: isMobile ? 1 : undefined }}
                >
                  비밀번호 변경
                </Button>
              </Stack>
            ) : (
              <Button 
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleConfirm}
              >
                확인
              </Button>
            )
          }
        >
          <Box sx={{ p: 0 }}>
            {/* 장기미접속자용 */}
            {notice && (
              <>
                <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={600} sx={{ mb: 1.5 }}>
                  개인정보 보호를 위해 비밀번호를 변경해 주세요.
                </Typography>
                <Typography sx={{ mb: 3 }}>
                  3개월 이상 동일한 비밀번호를 사용하고 있습니다. 회원님의 소중한 정보 보호를 지키기 위해 비밀번호를 주기적으로 변경해 주세요.
                </Typography>
              </>
            )}

            {/* 현재 비밀번호 */}
            <Typography component="label" htmlFor="oldpassword" fontWeight={500} sx={{ mb: 1 }}>
              현재 비밀번호 <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <TextField
              fullWidth
              id="oldpassword"
              placeholder="현재 비밀번호를 입력해 주세요"
              type={showOld ? 'text' : 'password'}
              sx={{ mb: 0.5 }}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              onBlur={oldPasswordValidate}
              error={oldPasswordError}
              helperText={oldPasswordHelperText}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ pr: 2 }}>
                    <IconButton onClick={() => setShowOld(!showOld)}>
                      {showOld ? <Visibility sx={{ color: 'grey.dark' }} /> : <VisibilityOff sx={{ color: 'grey.dark' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* 새 비밀번호 */}
            <Typography component="label" htmlFor="newpassword" fontWeight={500} sx={{ mt: 3, mb: 0.5 }}>
              새 비밀번호 <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <TextField
              fullWidth
              id="newpassword"
              placeholder="새로 사용하실 비밀번호를 입력해 주세요"
              type={showNew ? 'text' : 'password'}
              sx={{ mb: 1 }}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onBlur={newPasswordValidate}
              error={newPasswordError}
              helperText={newPasswordHelperText}
              FormHelperTextProps={{ sx: { ml: 0 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ pr: 2 }}>
                    <IconButton onClick={() => setShowNew(!showNew)}>
                      {showNew ? <Visibility sx={{ color: 'grey.dark' }} /> : <VisibilityOff sx={{ color: 'grey.dark' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormHelperText variant="body3" color="text.secondary" sx={{ mb: 3, mt: 0 }}>
              영문, 숫자, 특수문자를 조합해 10자 이상 입력( ! @ # $ % ^ & + = 가능)
            </FormHelperText>

            {/* 새 비밀번호 확인 */}
            <Typography component="label" htmlFor="newpasswordcheck" fontWeight={500} sx={{ mt: 3, mb: 0.5 }}>
              새 비밀번호 확인 <Typography component="span" color="error">*</Typography>
              <Box component="span" sx={visuallyHidden}>필수 입력</Box>
            </Typography>
            <TextField
              fullWidth
              id="newpasswordcheck"
              placeholder="새로 사용하실 비밀번호를 다시 입력해 주세요"
              type={showNewCheck ? 'text' : 'password'}
              sx={{ mb: 1 }}
              value={newPasswordCheck}
              onChange={(e) => setNewPasswordCheck(e.target.value)}
              onBlur={newPasswordCheckValidate}
              error={newPasswordCheckError}
              helperText={newPasswordCheckHelperText}
              FormHelperTextProps={{ sx: { ml: 0 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ pr: 2 }}>
                    <IconButton onClick={() => setShowNewCheck(!showNewCheck)}>
                      {showNewCheck ? <Visibility sx={{ color: 'grey.dark' }} /> : <VisibilityOff sx={{ color: 'grey.dark' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </BasicModal>
        <Box sx={{ display: alertOpen ? 'block' : 'none' }}>
          <AlertConfirmModal
            open={alertOpen}
            onClose={handleAlertClose}
            title="비밀번호 변경"
            description="비밀번호가 변경되었습니다. 새 비밀번호로 다시 로그인 해 주세요."
            type="alert"
            onConfirm={handleAlertClose}
          />
        </Box>
      </Box>
    </>
  )
}
